import React, { useEffect, useState } from 'react';
import NavBar from '../../components/Navbar/NavBar';
import Footer from '../../components/Footer';
import Card from '../../components/Cards/Card';
import SideMenu from '../../components/SideMenu';
import useDocTitle from '../../utils/useDocTitle';
import api from '../../utils/api.js';
import { useLanguage } from '../../context/LanguageContext';

const EducationalProgram = () => {
  const { language } = useLanguage(); // Use language context
  useDocTitle(language === 'English' ? 'Educational Programs' : 'Programet Edukative');

  const [mainPageData, setMainPageData] = useState(null);
  const [laboratories, setLaboratories] = useState([]);
  const [talksReadings, setTalksReadings] = useState([]);
  const [loading, setLoading] = useState(true);

  const menuItems = [
    { href: '#laboratories', label: language === 'English' ? 'Laboratories' : 'Laboratorët' },
    { href: '#talks-readings', label: language === 'English' ? 'Talks and Readings' : 'Bisedat dhe Seancat e Leximit' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch main page data
        const mainPageResponse = await api.get('/educational_programs');
        const mainData = mainPageResponse.data.find(
          (data) =>
            data.title.rendered ===
            (language === 'English' ? 'Educational Programs' : 'Programet Edukative')
        );

        if (mainData) {
          setMainPageData({
            title: mainData.title.rendered,
            mainText: mainData.content.rendered || '',
            mainImage: mainData.main_image?.guid || '/path/to/default-background.jpg',
          });
        }

        // Fetch laboratories and talks/readings data
        const [labsResponse, talksResponse] = await Promise.all([
          api.get('/laboratories'),
          api.get('/talks_readings'),
        ]);

        const filteredLaboratories = labsResponse.data.filter(
          (lab) => lab.page_language[0].name === language
        );

        const filteredTalksReadings = talksResponse.data.filter(
          (talk) => talk.page_language[0].name === language
        );

        setLaboratories(filteredLaboratories);
        setTalksReadings(filteredTalksReadings);
      } catch (error) {
        console.error('Error fetching educational programs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  if (loading) {
    return <div>{language === 'English' ? 'Loading...' : 'Duke u ngarkuar...'}</div>;
  }

  return (
    <>
      <NavBar />

      {mainPageData && (
        <div
          className="relative w-full h-96 bg-cover bg-center flex items-center justify-center"
          style={{
            backgroundImage: `url(${mainPageData.mainImage})`,
          }}
        >
          <div className="absolute inset-0 bg-black opacity-60"></div>
          <div className="relative z-10 text-center text-white px-4">
            <h1 className="text-5xl font-bold" tabIndex="0">
              {mainPageData.title}
            </h1>
            <p className="mt-4 text-lg">{mainPageData.mainText}</p>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row min-h-screen">
        <aside
          className="lg:w-1/4 bg-gray-100 p-4 lg:p-8 sticky top-16 lg:h-screen"
          aria-label={
            language === 'English'
              ? 'Sidebar navigation for Educational Programs'
              : 'Navigim anësor për Programet Edukative'
          }
        >
          <SideMenu
            backLink="/"
            backText={language === 'English' ? 'MAIN PAGE' : 'FAQJA KRYESORE'}
            menuItems={menuItems}
          />
        </aside>

        <div className="w-full lg:w-3/4 p-4 lg:p-8">
          <section id="laboratories" className="mb-16">
            <h2 className="text-2xl md:text-3xl font-semibold mb-6" tabIndex="0">
              {language === 'English' ? 'Laboratories' : 'Laboratorët'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
              {laboratories.map((lab) => (
                <Card
                  key={lab.id}
                  id={lab.id}
                  title={lab.title.rendered}
                  description={lab.content_description}
                  imgSrc={lab.image?.guid || '/path/to/default-image.jpg'}
                  imgAlt={lab.image?.post_excerpt || 'Laboratory image'}
                  linkText={language === 'English' ? 'Learn More' : 'Mëso më shumë'}
                  linkPath={`/resources/laboratories/${lab.id}`}
                  borderColorClass="border-secondary"
                  ariaLabel={
                    language === 'English'
                      ? `Go to Laboratory ${lab.title.rendered}`
                      : `Shko te Laboratori ${lab.title.rendered}`
                  }
                />
              ))}
            </div>
          </section>

          <section id="talks-readings" className="mb-16">
            <h2 className="text-2xl md:text-3xl font-semibold mb-6" tabIndex="0">
              {language === 'English' ? 'Talks and Readings' : 'Bisedat dhe Seancat e Leximit'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
              {talksReadings.map((talk) => (
                <Card
                  key={talk.id}
                  id={talk.id}
                  title={talk.title.rendered}
                  description={talk.content_description}
                  imgSrc={talk.card_image?.guid || '/path/to/default-image.jpg'}
                  imgAlt={talk.card_image?.post_excerpt || 'Talk image'}
                  linkText={language === 'English' ? 'Learn More' : 'Mëso më shumë'}
                  linkPath={`/resources/talks-reading/${talk.id}`}
                  borderColorClass="border-secondary"
                  ariaLabel={
                    language === 'English'
                      ? `Go to Talk & Reading Session ${talk.title.rendered}`
                      : `Shko te Biseda dhe Seanca ${talk.title.rendered}`
                  }
                />
              ))}
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default EducationalProgram;